import { useEffect } from 'react';
import { getTelegram, alertDialog } from '../telegram';
import { WebActionBar, WebBackButton } from '../components/WebNav';
import type { CartItem } from '../hooks/useCart';
import type { DepositAccount } from '../types';

const fmtPrice = (n: number) =>
  n.toLocaleString('ru-RU', { style: 'currency', currency: 'RUB', minimumFractionDigits: 2 });

const btnBase: React.CSSProperties = {
  border: 'none',
  borderRadius: 8,
  width: 32,
  height: 32,
  fontSize: 20,
  lineHeight: 1,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'inherit',
  fontWeight: 700,
};

interface Props {
  onBack: () => void;
  onCheckout: () => void;
  checkoutLoading?: boolean;
  items: CartItem[];
  add: (product: CartItem['product']) => void;
  dec: (productId: string) => void;
  totalItems: number;
  totalPrice: number;
  // Минимальная сумма заказа (руб.). 0 — без ограничения.
  minOrderAmount?: number;
  // Блок B: депозит кофейни, для которой оформляется заказ. null — точка ещё не
  // выбрана (несколько кофеен) или депозиты не загрузились: тогда гейт не рисуем,
  // бэкенд всё равно проверит при оформлении.
  deposit?: DepositAccount | null;
  depositLoading?: boolean;
  onTopUp?: () => void;
  // Блок D: переход на страницу «Депозит» (операции и счета).
  onDeposit?: () => void;
}

export default function CartPage({
  onBack, onCheckout, checkoutLoading, items, add, dec, totalItems, totalPrice,
  minOrderAmount = 0, deposit = null, depositLoading = false, onTopUp, onDeposit,
}: Props) {

  // Заказ ниже минимальной суммы — оформление блокируется.
  const belowMin = minOrderAmount > 0 && totalPrice < minOrderAmount;
  const limitLabel = minOrderAmount.toLocaleString('ru-RU');
  const minMessage =
    `Ваша текущая заявка менее ${limitLabel} руб. Пожалуйста, закажите блюда ` +
    `на сумму, превышающую минимальный заказ в ${limitLabel} руб.`;

  // Блок B: строгая предоплата — доступный остаток депозита должен покрывать
  // минимальный заказ и сумму корзины (docs/deposits-spec.md, раздел 3).
  const available = deposit ? Number(deposit.available) : null;
  let depositMessage = '';
  if (deposit && !belowMin) {
    if (!deposit.can_order) {
      depositMessage = deposit.block_reason ?? 'Депозит не позволяет оформить заказ.';
    } else if (available !== null && totalPrice > available) {
      depositMessage =
        `Сумма заказа ${fmtPrice(totalPrice)} превышает доступный остаток депозита ` +
        `${fmtPrice(available)}. Пополните депозит минимум на ${fmtPrice(totalPrice - available)}.`;
    }
  }
  const depositShort = depositMessage !== '';

  // BackButton
  useEffect(() => {
    const tg = getTelegram();
    if (!tg) return;
    tg.BackButton.show();
    tg.BackButton.onClick(onBack);
    return () => { tg.BackButton.offClick(onBack); tg.BackButton.hide(); };
  }, [onBack]);

  // MainButton — оформить заказ. Ниже минимума — подсказка; не хватает депозита —
  // открываем пополнение; иначе оформляем.
  useEffect(() => {
    const tg = getTelegram();
    if (!tg) return;
    const handleMain = () => {
      if (belowMin) { alertDialog(minMessage); return; }
      if (depositShort) {
        if (onTopUp) onTopUp(); else alertDialog(depositMessage);
        return;
      }
      onCheckout();
    };
    if (belowMin) {
      const left = fmtPrice(minOrderAmount - totalPrice);
      tg.MainButton.setText(`Минимум ${limitLabel} ₽ · не хватает ${left}`);
    } else if (depositShort) {
      tg.MainButton.setText('💳 Пополнить депозит');
    } else {
      tg.MainButton.setText(`Оформить заказ · ${fmtPrice(totalPrice)}`);
    }
    tg.MainButton.show();
    if (checkoutLoading) {
      tg.MainButton.showProgress(false);
      tg.MainButton.offClick(handleMain);
    } else {
      tg.MainButton.hideProgress();
      tg.MainButton.onClick(handleMain);
    }
    return () => { tg.MainButton.offClick(handleMain); tg.MainButton.hideProgress(); tg.MainButton.hide(); };
  }, [checkoutLoading, onCheckout, onTopUp, totalPrice, belowMin, depositShort, depositMessage, minOrderAmount, limitLabel, minMessage]);

  // Веб-обработчик кнопки «Оформить»: та же логика, что у MainButton.
  const handleWebCheckout = () => {
    if (belowMin) { alertDialog(minMessage); return; }
    if (depositShort) {
      if (onTopUp) onTopUp(); else alertDialog(depositMessage);
      return;
    }
    onCheckout();
  };

  if (items.length === 0) {
    return (
      <div className="container" style={{ padding: '16px' }}>
        <WebBackButton onClick={onBack} label="‹ В каталог" />
        <h3 style={{ margin: '0 0 16px' }}>Корзина</h3>
        <p className="hint">Корзина пуста</p>
      </div>
    );
  }

  return (
    <div className="container" style={{ padding: '16px 16px 80px' }}>
      <WebBackButton onClick={onBack} label="‹ В каталог" />
      <h3 style={{ margin: '0 0 16px', fontSize: 18 }}>
        Корзина · {totalItems} шт
      </h3>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.map(({ product: p, quantity: qty }) => (
          <div key={p.id} className="card">
            <div style={{ fontWeight: 600, fontSize: 15 }}>{p.name}</div>
            <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button
                  onClick={() => dec(p.id)}
                  style={{ ...btnBase, background: 'var(--tg-secondary-bg)', color: 'var(--tg-text)' }}
                >
                  −
                </button>
                <span style={{ minWidth: 24, textAlign: 'center', fontWeight: 700 }}>{qty}</span>
                <button
                  onClick={() => add(p)}
                  style={{ ...btnBase, background: 'var(--tg-button)', color: '#fff' }}
                >
                  +
                </button>
              </div>
              <span style={{ fontWeight: 700, color: 'var(--tg-button)' }}>
                {fmtPrice(Number(p.price_with_vat) * qty)}
              </span>
            </div>
          </div>
        ))}
      </div>

      <div style={{
        marginTop: 20,
        padding: '16px',
        background: 'var(--tg-secondary-bg)',
        borderRadius: 14,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <span style={{ fontWeight: 600 }}>Итого</span>
        <span style={{ fontWeight: 700, fontSize: 18, color: 'var(--tg-button)' }}>
          {fmtPrice(totalPrice)}
        </span>
      </div>

      {/* Блок B: состояние депозита кофейни + кнопка пополнения */}
      {deposit && (
        <div style={{ marginTop: 12, padding: '12px 14px', borderRadius: 12, background: 'var(--tg-secondary-bg)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14 }}>
              💳 Депозит <span className="hint">· {deposit.coffee_shop_name}</span>
            </span>
            <span style={{ fontWeight: 700, color: depositShort ? '#ff3b30' : 'var(--tg-button)' }}>
              {fmtPrice(Number(deposit.available))}
            </span>
          </div>
          {Number(deposit.reserved) > 0 && (
            <div className="hint" style={{ marginTop: 2 }}>
              в резерве под заказы в работе: {fmtPrice(Number(deposit.reserved))}
            </div>
          )}
          {onTopUp && (
            <button
              onClick={onTopUp}
              style={{
                marginTop: 10, width: '100%', padding: '10px', border: 'none', borderRadius: 10,
                background: 'var(--tg-bg)', color: 'var(--tg-button)', fontSize: 14, fontWeight: 700,
                fontFamily: 'inherit', cursor: 'pointer',
              }}
            >
              Пополнить депозит
            </button>
          )}
          {onDeposit && (
            <button
              onClick={onDeposit}
              style={{
                marginTop: 6, width: '100%', padding: '6px', border: 'none', background: 'transparent',
                color: 'var(--tg-button)', fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
              }}
            >
              Операции и счета ›
            </button>
          )}
        </div>
      )}
      {!deposit && depositLoading && (
        <p className="hint" style={{ marginTop: 10 }}>Проверяем депозит…</p>
      )}

      {(belowMin || depositShort) && (
        <div style={{
          marginTop: 12, padding: '12px 14px', borderRadius: 12,
          background: 'rgba(255,59,48,0.10)', color: '#ff3b30', fontSize: 14, fontWeight: 600,
        }}>
          {belowMin ? minMessage : depositMessage}
        </div>
      )}

      <WebActionBar
        label={
          belowMin
            ? `Минимум ${limitLabel} ₽ · не хватает ${fmtPrice(minOrderAmount - totalPrice)}`
            : depositShort
              ? '💳 Пополнить депозит'
              : checkoutLoading
                ? 'Оформляем…'
                : `Оформить заказ · ${fmtPrice(totalPrice)}`
        }
        onClick={handleWebCheckout}
        disabled={checkoutLoading}
        variant={belowMin ? 'muted' : 'primary'}
      />
    </div>
  );
}
